import { forDisplay } from './displayName';

/**
 * The one line *09 · Ready* puts in its callout — **“Front desk” · connected to Allentown, PA · DC 3.**
 *
 * ⭐ **Both facts, assembled from what the server actually answered** [AC-5]: the name is the one
 * `PUT /enrolment/name` stored, and the venue is the `displayName` the connect reported — the frame's sample
 * is byte-identical to the api's own `EnrolledVenueModel` example, so the separator inside *Allentown, PA ·
 * DC 3* is part of the venue's name and not part of this sentence.
 *
 * ⛔ **Nothing is invented when a part is missing.** The venue name can be absent — a 200 whose body does
 * not carry one is still an enrolment [`services/enrolment.ts`] — and the answer is the shorter sentence,
 * never a placeholder venue and never a blank *"connected to "*. A kiosk with no name at all cannot reach
 * this screen through the flow; `undefined` says so, and the screen renders no callout rather than an empty
 * one.
 */
export function readySummary(
  kioskName: string | undefined,
  venueName: string | undefined,
): string | undefined {
  if (kioskName === undefined) return undefined;

  /*
   * ⭐ **Both halves go through `forDisplay`**, the venue's as well as the kiosk's: an invisible bidi
   * override in EITHER makes this sentence assert a binding that was never established, and it is the one
   * sentence a staffer reads before walking away. ⛔ The stored value is untouched [0118].
   */
  // Curly quotes, as the frame draws them.
  const parts = [`“${forDisplay(kioskName)}”`];
  if (venueName !== undefined) parts.push(`connected to ${forDisplay(venueName)}`);

  return `${parts.join(' · ')}.`;
}
